/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {loadAllDeviceFiles} from '@ion/core/src/services/Connect';
import {DeviceFile, EntityStatus} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {
  createKey,
  QueryKey
} from '../config/QueryKeys';
import {
  createQueryResult,
  invalidateQueryCache
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceFilesResult = UseDefaultQueryResult & {
  deviceFiles: DeviceFile[];
  key: string[]
};

/**
 * The useDeviceFiles hook provides a React Query hook for referencing device files.
 *
 * ```typescript
 *
 * import {useDeviceFiles} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {deviceFiles} = useDeviceFiles();
 *
 * ```
 *
 * @param   accountId ID of the account within which to perform the device file search.
 * @param   platformIds Platform IDs (device types + file types) to use when performing
 *          the device file search.
 * @param   appIds App IDs to use when performing the device file search.
 * @param   includeSubAccounts Whether to search sub-accounts of the main account.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to Infinity.
 * @returns UseDeviceFilesResult containing the query results and the device files.
 */
export const useDeviceFiles = ({
  accountId,
  suppressNullVersions = false,
  enabledOnly = false,
  devicePlatformIds = [],
  platformIds = [],
  appIds = [],
  includeSubAccounts = false,
  enabled = true,
  staleTime = Infinity
}): UseDeviceFilesResult => {
  const key = useMemo(() => {
    return createKey(
      QueryKey.DEVICE_FILES,
      accountId,
      devicePlatformIds,
      platformIds,
      appIds,
      includeSubAccounts
    );
  }, [
    accountId,
    safeJSONStringify(devicePlatformIds),
    safeJSONStringify(platformIds),
    safeJSONStringify(appIds),
    includeSubAccounts
  ]);

  const query = useQuery(key, async() => {
    const results = await loadAllDeviceFiles({
      pageId           : 1,
      pageSize         : 100,
      sortOn           : ['+validationMetaData.fields.version'],
      status           : enabledOnly ? EntityStatus.Enabled : undefined,
      filterOn         : createFilter(platformIds, appIds, devicePlatformIds),
      includeSubGroups : includeSubAccounts,
      accountId,
      suppressNullVersions
    }) as DeviceFile[];
    return results;
  }, {staleTime: staleTime as number, enabled});

  return createQueryResult(query, {
    deviceFiles : query?.data || [],
    key
  }) as UseDeviceFilesResult;
};

export const invalidateDeviceFiles = (key = QueryKey.DEVICE_FILES) => {
  invalidateQueryCache(key);
};

export const invalidatePreCachedDeviceFiles = (key: string[]) => {
  invalidateQueryCache(key);
};

/**
 * Creates a search filter for fetching device files based on the specified platform ID
 * and app IDs (as well as always being valid).
 */
const createFilter = (platformIds: string[], appIds: string[], devicePlatformIds: number[]) => {
  const filter = [];
  if (devicePlatformIds?.length && platformIds?.length) {
    devicePlatformIds.forEach(id => {
      platformIds.forEach(platformId => {
        filter.push({
          ...createVbusFilter(id),
          ...createPlatformIdFilter(platformId)
        }, {
          ...createPlatformIdFilter(parseInt(platformId))
        });
      });
    });
  } else if (platformIds?.length && appIds?.length) {
    platformIds.forEach(platformId => {
      appIds.forEach(appId => {
        filter.push({
          ...createPlatformIdFilter(platformId),
          ...createAppIdFilter(appId)
        }, {
          ...createPlatformIdFilter(parseInt(platformId))
        });
      });
    });
  } else if (platformIds?.length) {
    platformIds.forEach(platformId => {
      filter.push({
        ...createPlatformIdFilter(platformId)
      }, {
        ...createPlatformIdFilter(parseInt(platformId))
      });
    });
  } else if (appIds?.length) {
    appIds.forEach(appId => {
      filter.push({
        ...createAppIdFilter(appId)
      });
    });
  };
  return {
    validationStatus : '{\"status\":\"VALID\"}',
    ...(filter.length && {validationMetaData: safeJSONStringify(filter)})
  };
};

const createPlatformIdFilter = (platformId: string | number) => ({platformId});

const createAppIdFilter = (appId: string) => ({
  fields : {
    appId : [{value: `${appId}`}]
  }
});

const createVbusFilter = (platformId: string | number) => ({
  fields : {
    vBus : [{id: platformId}]
  }
});

export default useDeviceFiles;
